{
  const FOCUSABLE = [
    'a[href]',
    'button:not([disabled])',
    'input:not([disabled])',
    'select:not([disabled])',
    'textarea:not([disabled])',
    '[tabindex]:not([tabindex="-1"])'
  ].join(',');

  // Only the topmost entry handles Tab — prevents nested traps from fighting each other.
  const trapStack = [];

  function getFocusable(container) {
    return [...container.querySelectorAll(FOCUSABLE)].filter(el => {
      let node = el;
      while (node) {
        if (node.hasAttribute('inert')) return false;
        if (node.getAttribute('aria-hidden') === 'true') return false;
        if (node.hasAttribute('hidden')) return false;
        const cs = getComputedStyle(node);
        if (cs.display === 'none' || cs.visibility === 'hidden') return false;
        if (node === container) break;
        node = node.parentElement;
      }
      return true;
    });
  }

  // Calls cb for EVERY element matching selector — now and as new ones appear in the DOM.
  // querySelector only returns the first match; class selectors need querySelectorAll.
  function forEachReady(selector, cb) {
    const seen = new WeakSet();
    function process() {
      document.querySelectorAll(selector).forEach(el => {
        if (!seen.has(el)) { seen.add(el); cb(el); }
      });
    }
    process();
    new MutationObserver(process).observe(document.body, { childList: true, subtree: true });
  }

  function isHidden(el, options) {
    if (options.openClass) return !el.classList.contains(options.openClass);
    if (el.hasAttribute('hidden') || el.hasAttribute('inert')) return true;
    if (getComputedStyle(el).display === 'none') return true;
    return false;
  }

  function setupFocusTrap(selector, options = {}) {
    forEachReady(selector, el => {
      let trapped = false;
      let prevFocus = null;
      let retryTimer = null;

      function trapTab(e) {
        if (e.key !== 'Tab') return;
        // Yield to any trap opened on top of this one (e.g. sub-panel inside CartDrawer).
        if (trapStack[trapStack.length - 1] !== el) return;
        const focusable = getFocusable(el);
        // No focusable elements yet (mid-transition) — eat Tab so focus can't escape.
        if (!focusable.length) { e.preventDefault(); return; }
        const first = focusable[0];
        const last = focusable[focusable.length - 1];
        const cur = document.activeElement;
        const outside = !el.contains(cur);
        if (e.shiftKey ? (outside || cur === first) : (outside || cur === last)) {
          e.preventDefault();
          (e.shiftKey ? last : first).focus();
        }
      }

      // Retry initial focus for up to ~1 second (20 × 50ms) to ride out CSS transitions.
      function tryFocus(attempt) {
        if (!trapped) return;
        const focusable = getFocusable(el);
        if (focusable.length) {
          focusable[0].focus();
        } else if (attempt < 20) {
          retryTimer = setTimeout(() => tryFocus(attempt + 1), 50);
        }
      }

      function activate() {
        if (trapped) return;
        trapped = true;
        trapStack.push(el);
        prevFocus = document.activeElement;
        el.setAttribute('aria-hidden', 'false');
        document.addEventListener('keydown', trapTab);
        tryFocus(0);
      }

      function deactivate() {
        if (!trapped) return;
        trapped = false;
        clearTimeout(retryTimer);
        const idx = trapStack.indexOf(el);
        if (idx !== -1) trapStack.splice(idx, 1);
        document.removeEventListener('keydown', trapTab);
        el.setAttribute('aria-hidden', 'true');
        prevFocus?.focus();
        prevFocus = null;
      }

      function check() {
        isHidden(el, options) ? deactivate() : activate();
      }

      // Watch the element itself. Exclude aria-hidden from filter — we set it
      // ourselves and reacting to it would cause activate/deactivate loops.
      new MutationObserver(check).observe(el, {
        attributes: true,
        attributeFilter: ['style', 'class', 'hidden', 'inert']
      });

      // Also observe up to 3 ancestor levels. Themes that toggle visibility on a
      // wrapper (not the trap element itself) will still trigger check().
      // getComputedStyle(el) inside isHidden() inherits parent display:none,
      // so the isHidden result is correct — we just need the mutation to fire it.
      let anc = el.parentElement;
      for (let i = 0; i < 3 && anc && anc !== document.body; i++, anc = anc.parentElement) {
        new MutationObserver(check).observe(anc, {
          attributes: true,
          attributeFilter: ['style', 'class', 'hidden', 'inert']
        });
      }
    });
  }

  // there is also a `button#mobile-navigation` which is illegal HTML (duplicate IDs) — cannot fix that here
  [
    ['#QuickView'],
    ['#CartDrawer'],
    ['div#mobile-navigation', { openClass: 'menu-opening' }],
    ['#social-feeds-popup-actual'],
    ['#ProductFilter'],
    ['.hard-focus-trap'],
  ].forEach(([selector, opts]) => setupFocusTrap(selector, opts));
}
